// angular imports
import { Component, Input, SimpleChanges, Output, EventEmitter, OnChanges, ViewChild, OnInit } from '@angular/core';

// cc-imports
import { IconConstants, Utils } from 'cc-common';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';

// Service
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { CommunityService } from '../../../_services/data-services/community.service';

// Models
import { WFVendorHistoryParametersDto } from '../../_models/wf-vendor-history-parameters-dto';
import { WFInvoiceHistoryParametersDto } from '../../_models/wf-invoice-history-parameters-dto';
import { WFInvoiceHistoryResultDto } from '../../_models/wf-invoice-history-result-dto';
import { WorkFlowParametersDto } from '../../_models/workflow-parameters-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';

// Constant imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';

@Component({
    selector: 'app-workflow-iva-history',
    templateUrl: './workflow-iva-history.component.html',
    styleUrls: ['./workflow-iva-history.component.scss']
})
export class WorkflowIvaHistoryComponent implements OnInit, OnChanges {
    @ViewChild('historyPopup', { static: false }) historyPopup: DxPopupComponent;

    @Input() mode: string;
    @Input() title: string;
    @Input() payee: string;
    @Input() communityId: number;
    @Input() vendorAccountNumber: string;
    @Input() vendorHistoryBaseParams: WFVendorHistoryParametersDto;
    @Input() vendorAccountHistoryBaseParams: WFVendorHistoryParametersDto;
    @Input() invoiceHistoryBaseParams: WFInvoiceHistoryParametersDto;
    @Input() workflowId: number;

    @Output() closeIVAHistory = new EventEmitter<any>();

    visible = false;

    communityName: string;
    managementCompanyId: number;

    gridSource: WFInvoiceHistoryResultDto[];
    selectedRecord: WFInvoiceHistoryResultDto;
    hasGridSourceData = false;
    showNotesPopup = false;
    loadingVisible = false;
    noDataText: string;
    notesTitle: string;
    previousNotes: string;
    payeeLabel: string;
    currencyFormat: string;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE_GRID;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconHistory = IconConstants.HISTORY;

    showWorkflowHistory = false;
    selectedHistoryRecord: WFInvoiceHistoryResultDto;

    showPaymentHistory = false;

    dateFormat: string;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly communityService: CommunityService
    ) {
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
        this.dateFormat = StringFormatConstants.dateFormat;
    }

    ngOnInit() {
        this.show();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.mode && changes.mode.currentValue) {
            this.mode = changes.mode.currentValue;
        }

        if (changes.title && changes.title.currentValue) {
            this.title = changes.title.currentValue;
        }

        if (changes.payee && changes.payee.currentValue) {
            this.payee = changes.payee.currentValue;
        }

        if (changes.vendorHistoryBaseParams && changes.vendorHistoryBaseParams.currentValue) {
            this.vendorHistoryBaseParams = changes.vendorHistoryBaseParams.currentValue;
        }

        if (changes.vendorAccountHistoryBaseParams && changes.vendorAccountHistoryBaseParams.currentValue) {
            this.vendorAccountHistoryBaseParams = changes.vendorAccountHistoryBaseParams.currentValue;
        }

        if (changes.invoiceHistoryBaseParams && changes.invoiceHistoryBaseParams.currentValue) {
            this.invoiceHistoryBaseParams = changes.invoiceHistoryBaseParams.currentValue;
        }
        this.selectHistoryView();
    }

    show(): void {
        this.visible = true;
    }

    onNotesClick(row): void {
        this.selectedRecord = row;
        this.showNotesPopup = true;
    }

    onHistoryNotesPopClose(): void {
        this.showNotesPopup = false;
    }

    onHistoryPopUpShown(): void {
        this.historyPopup.instance.repaint();
        this.getCommunities();
    }

    onHistoryPopUpClose(): void {
        this.gridSource = [];
        this.hasGridSourceData = false;
        this.closeIVAHistory.emit(true);
    }

    onDownloadFile(row): void {
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = row.workflowId;
        baseParams.communityId = this.communityId;
        this.selectedRecord = row;

        this.downloadInvoice(baseParams);
    }

    onWorkflowHistoryClick(record: WFInvoiceHistoryResultDto): void {
        this.selectedHistoryRecord = record;
        this.showWorkflowHistory = true;
    }

    onWorkflowHistoryClose(): void {
        this.showWorkflowHistory = false;
    }

    onPaymentHistoryClick(record: WFInvoiceHistoryResultDto): void {
        this.selectedHistoryRecord = record;
        this.showPaymentHistory = true;
    }

    onPaymentHistoryClose(): void {
        this.showPaymentHistory = false;
    }

    isPaymentCleared(record: WFInvoiceHistoryResultDto): boolean {
        return record.paymentCleared && (record.status === 'Paid' || record.status === 'Partially Paid');
    }

    private getCommunities(): void {
        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                if (result.length > 0) {
                    const selectedCommunity = result.find((d) => d.communityId === this.communityId);
                    this.communityName = selectedCommunity.communityName;
                    this.managementCompanyId = selectedCommunity.managementCompanyId;
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private downloadInvoice(baseParams: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(fileURL, '_blank', 'location=yes, scrollbars=yes,status=yes');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.updateSubjectErrorMessage('File not available.');
                this.loadingVisible = false;
            }
        );
    }

    private selectHistoryView(): void {
        this.hasGridSourceData = false;
        this.resetAllFields();

        if (this.mode === 'vendorHistory') {
            this.payeeLabel = 'Vendor';
            this.getVendorHistory();
        }

        if (this.mode === 'vendorAccountHistory') {
            this.payeeLabel = 'Vendor';
            this.getVendorAccountHistory();
        }

        if (this.mode === 'invoiceHistory') {
            this.payeeLabel = 'Payee';
            this.getInvoiceHistory();
        }
    }

    private getInvoiceHistory(): void {
        this.loadingVisible = true;
        if (Utils.notNullOrEmpty(this.workflowId)) {
            this.invoiceHistoryBaseParams.workflowId = this.workflowId;
        }
        this.workflowSharedService.getWorkflowInvoiceHistory(this.invoiceHistoryBaseParams).subscribe(
            (result: WFInvoiceHistoryResultDto[]) => {
                if (result.length > 0) {
                    this.hasGridSourceData = true;
                    this.gridSource = result;
                } else {
                    this.hasGridSourceData = false;
                    this.noDataText = 'There are no matching invoices for the payee.';
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getVendorAccountHistory(): void {
        this.loadingVisible = true;
        if (Utils.notNullOrEmpty(this.workflowId)) {
            this.vendorAccountHistoryBaseParams.workflowId = this.workflowId;
        }
        this.workflowSharedService.getWorkflowVendorAccountHistory(this.vendorAccountHistoryBaseParams).subscribe(
            (result: WFInvoiceHistoryResultDto[]) => {
                if (result.length > 0) {
                    this.hasGridSourceData = true;
                    this.gridSource = result;
                } else {
                    this.hasGridSourceData = false;
                    this.noDataText = Utils.notNullOrEmpty(this.vendorAccountNumber)
                        ? `There are no matching invoices for the vendor account.<strong>${this.vendorAccountNumber}</strong>.`
                        : `There are no matching invoices for the vendor account.`;
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getVendorHistory(): void {
        this.loadingVisible = true;

        this.workflowSharedService.getWorkflowVendorHistory(this.vendorHistoryBaseParams).subscribe(
            (result: WFInvoiceHistoryResultDto[]) => {
                if (result.length > 0) {
                    this.hasGridSourceData = true;
                    this.gridSource = result;
                } else {
                    this.hasGridSourceData = false;
                    this.noDataText = 'There are no matching invoices for the vendor.';
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private resetAllFields(): void {
        this.gridSource = [];
        this.hasGridSourceData = false;
        this.showNotesPopup = false;
        this.noDataText = null;
        this.notesTitle = null;
    }
}
